import { NextPage } from 'next';
import CategoryPage, { Props } from 'src/components/pages/CategoryPage/CategoryPage';
import { getCategoryUrl, getPageMustBeReset } from 'src/modules/Categories';
import prepareCategoryData from 'src/redux/catalogue/catalogueOperations/prepareCategoryData';
import { selectSelectedCategory } from 'src/redux/catalogue/selectors';
import { NextPageContextWithStore } from 'src/redux/initializeStore';
import getQueryWithMergedFilterParams from 'src/utils/filter/getQueryWithMergedFilterParams';
import { getUserPreferencesFromCookies } from 'src/utils/pages/_app/_appUtils';
import { getIsServer } from 'src/utils/system/getIsServer';
import makeSearchString from 'src/utils/system/makeSearchString';

const Category: NextPage<Props> = (props) => {
  return <CategoryPage {...props} />;
};

const excludedParams = ['lang', 'category-id'];

Category.getInitialProps = async (context: NextPageContextWithStore) => {
  const {
    store,
    query,
    res,
    req,
  } = context;

  if (res?.statusCode === 404) {
    /**
     * @description We don't need next getInitialProps execution if statusCode = 404
     * because it's due to _app.getInitialProps -> getRedirectPath() returned NOT_FOUND
     * and the render of the 404 page occurs in the _app
     */
    return {} as Props;
  }

  const categoryId = query['category-id'] as string;
  const isServer = getIsServer();
  const { storeId } = getUserPreferencesFromCookies(req);

  if (isServer) {
    const page = query.page as string;
    const pageNum = Number(page);
    const pageIsInvalid = (page && isNaN(pageNum)) || pageNum === 0;

    if (pageIsInvalid) {
      res.statusCode = 404;
      return {
        dataPreparedOnSSR: isServer,
        pageIsNotFound: true,
      };
    }

    // preparation to return to the client
    const queryWithMergedFilterParams = getQueryWithMergedFilterParams(query);
    const searchString = makeSearchString(queryWithMergedFilterParams, excludedParams);

    await store.dispatch(prepareCategoryData({ categoryId, searchString, storeId }));

    const state = store.getState();
    const selectedCategory = selectSelectedCategory(state);
    const categoryData = state.catalogue.categoryData;
    const language = state.app.language;

    const errorFetchingCategory = state.catalogue.errorFetchingCategory;
    const errorIs404 = errorFetchingCategory?.status >= 400 && errorFetchingCategory?.status < 500;

    if (selectedCategory === null || errorIs404) {
      res.statusCode = 404;
      return {
        dataPreparedOnSSR: isServer,
        pageIsNotFound: true,
      };
    }

    const errorIs500 = errorFetchingCategory?.status >= 500 && errorFetchingCategory?.status < 600;

    if (errorIs500) {
      res.statusCode = 500;
      return {
        dataPreparedOnSSR: isServer,
        internalServerError: true,
      };
    }

    const pageMustBeReset = getPageMustBeReset({
      categoryData,
      query,
    });

    if (pageMustBeReset) {
      const queryParamsWithoutPage = makeSearchString(
        queryWithMergedFilterParams,
        [...excludedParams, 'page'],
      ).split('?')[1];

      const newUrl = getCategoryUrl({
        language,
        categoryId,
        queryParams: queryParamsWithoutPage,
      });

      res.writeHead(302, { Location: newUrl });
      res.end();
    }
  }

  return {
    dataPreparedOnSSR: isServer,
  };
};

export default Category;
